import type { API, TRequestsHandler } from '~/utils/requestsProcessor/types';

export class CraftingImpl {
  constructor(private readonly makeRequest: TRequestsHandler) {}

  /** GET /crafting/tokens */
  // Sender = address
  public getCraftingTokens(query: { sender: string; offset?: number; limit?: number }) {
    return this.makeRequest<API.CraftingGetTokensResponse>(`crafting/tokens`, {
      method: 'GET',
      query
    });
  }

  /** PATCH /crafting/tokens/{tokenAddress} */
  public patchCraftingTokensByTokenAddress(
    tokenAddress: string,
    body: { tokenImageUrl: string },
    query: { sender: string }
  ) {
    return this.makeRequest<API.CraftingUpdateTokenResponse>(
      `crafting/tokens/${encodeURIComponent(String(tokenAddress))}`,
      {
        method: 'PATCH',
        query,
        body
      }
    );
  }

  /** POST /crafting/activity */
  public postCraftingActivity(body: {
    recipeId: string;
    transactionHash: string;
    userAddress: string;
    landBuildingHash: string;
  }) {
    return this.makeRequest<API.CraftingCreateActivityResponse>(`crafting/activity`, {
      method: 'POST',
      body
    });
  }

  /** GET /crafting/recipes/{recipeId} */
  public getCraftingRecipesByRecipeId(recipeId: string) {
    return this.makeRequest<API.CraftingGetRecipeResponse>(`crafting/recipes/${encodeURIComponent(String(recipeId))}`, {
      method: 'GET'
    });
  }

  /** GET /crafting/recipes */
  public getCraftingRecipes(query?: { offset?: number; limit?: number }) {
    return this.makeRequest<API.CraftingGetRecipesResponse[]>(`crafting/recipes`, {
      method: 'GET',
      query
    });
  }

  /** GET /crafting/recipes/pending */
  public getCraftingRecipesPending(query: { userAddress: string; buildingHash: string; recipeId: number }) {
    return this.makeRequest<API.CraftingGetPendingActivitiesResponse>(`crafting/recipes/pending`, {
      method: 'GET',
      query
    });
  }

  /** GET /crafting/activity/recipe/{recipeId} */
  public getCraftingActivityRecipeByRecipeId(recipeId: string) {
    return this.makeRequest<API.CraftingRecipeWithTokensEntity>(
      `crafting/activity/recipe/${encodeURIComponent(String(recipeId))}`,
      {
        method: 'GET'
      }
    );
  }

  /** GET /crafting/activity/user/{userAddress} */
  public getCraftingActivityUserByUserAddress(userAddress: string) {
    return this.makeRequest<API.CraftingActivityEntity[]>(
      `crafting/activity/user/${encodeURIComponent(String(userAddress))}`,
      {
        method: 'GET'
      }
    );
  }

  /** GET /crafting/buildings/{buildingHash}/recipes */
  public getCraftingBuildingsByBuildingHashRecipes(buildingHash: string) {
    return this.makeRequest<API.CraftingGetRecipesByBuildingResponse>(
      `crafting/buildings/${encodeURIComponent(String(buildingHash))}/recipes`,
      {
        method: 'GET'
      }
    );
  }

  /** GET /crafting/buildings-hashes */
  public getCraftingBuildingsHashes(query: { productAddress: string }) {
    return this.makeRequest<API.GetBuildingHashesByProductTokenAddressResponse>(`crafting/buildings-hashes`, {
      method: 'GET',
      query
    });
  }

  /** GET /crafting/recipes/unclaimed */
  public getCraftingRecipesUnclaimed(query: { userAddress: string; buildingHash?: string }) {
    return this.makeRequest<API.CraftingGetUnclaimedRecipesByUserResponse[]>(`crafting/recipes/unclaimed`, {
      method: 'GET',
      query
    });
  }

  /** GET /crafting/recipes/in-queue */
  public getCraftingRecipesInQueue(query: { recipeId?: string; buildingHash: string }) {
    return this.makeRequest<API.CraftingGetRecipeInQueueByIdResponse[]>(`crafting/recipes/in-queue`, {
      method: 'GET',
      query
    });
  }

  /** PATCH /crafting/buildings/{buildingId} */
  public patchCraftingBuildingsByBuildingId(
    buildingId: string,
    body: { buildingKey: string; buildingImageUrl: string },
    query: { sender: string }
  ) {
    return this.makeRequest<API.CraftingUpdateBuildingResponse>(
      `crafting/buildings/${encodeURIComponent(String(buildingId))}`,
      {
        method: 'PATCH',
        query,
        body
      }
    );
  }

  /** GET /crafting/buildings/{buildingId} */
  public getCraftingBuildingsByBuildingId(buildingId: number) {
    return this.makeRequest<API.CraftingGetBuildingResponse>(
      `crafting/buildings/${encodeURIComponent(String(buildingId))}`,
      {
        method: 'GET'
      }
    );
  }

  /** GET /crafting/buildings */
  public getCraftingBuildings(query?: { limit?: number; offset?: number; withRecipes?: boolean }) {
    return this.makeRequest<API.CraftingGetBuildingsResponse>(`crafting/buildings`, {
      method: 'GET',
      query: query
    });
  }
}
